/**
 * VideoPlayer - Plyr-based video player with popup and background modes.
 * Custom element: <video-player>
 *
 * Attributes (from $info):
 *   background     Boolean  -
 *   unmute         Boolean  -
 *   id             String   -
 *   plyer-wrapper  Boolean  true
 *
 * Refs:
 *   [data-ref="video"]  (optional)
 *   [data-ref="trigger"]  (optional)
 *   [data-ref="close"]  (optional)
 *   [data-ref="popup"]  (optional)
 *
 * Loading: lazy-loaded by the theme loader (theme.js), which imports this
 * file by its exact name - the filename is load-bearing (import map in
 * snippets/theme-head.liquid).
 * Reconstructed to readable identifiers from the original minified vendor
 * bundle; behaviour verified unchanged by AST alpha-equivalence.
 */
var defineProperty = Object.defineProperty;
var setFunctionName = (target, functionName) =>
  defineProperty(target, "name", {
    value: functionName,
    configurable: !0,
  });
import { ThemeComponent } from "./theme.js";
import { Plyr } from "./vendor.js";
class VideoPlayer extends ThemeComponent {
  init() {
    if (!this.video) return;
    this.hydrateDeferredIframe().then(() => {
      if (this.plyerWrapper) this.handlePlyrInit();
      this.setEventListeners();
    });
  }
  getIframe() {
    return this.video instanceof HTMLIFrameElement
      ? this.video
      : this.video?.querySelector?.("iframe");
  }
  hydrateDeferredIframe() {
    const iframe = this.getIframe();
    if (!iframe?.dataset?.src) return Promise.resolve();
    const activate = () => {
      if (!iframe.dataset.src) return;
      iframe.src = iframe.dataset.src;
      delete iframe.dataset.src;
    };
    if (this.background) {
      activate();
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      if (!("IntersectionObserver" in window)) {
        activate();
        resolve();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          activate();
          observer.disconnect();
          resolve();
        },
        { rootMargin: "200px 0px" },
      );
      observer.observe(this);
    });
  }
  handlePlyrInit() {
    let controls = ["play"];
    (this.unmute && (controls = ["play", "mute"]),
      this.background
        ? (this.player = new Plyr(this.video, {
            iconPrefix: `${this.id}--plyr`,
            muted: !0,
            autoplay: !0,
            controls: controls,
            loadSprite: !1,
          }))
        : (this.player = new Plyr(this.video, {
            iconPrefix: `${this.id}--plyr`,
          })));
  }
  setEventListeners() {
    this.trigger &&
      (this.$listen(this.trigger, "click", () => {
        (this.handlePopup(),
          document.body.addEventListener("keydown", (keyEvent) => {
            keyEvent.key === "Escape" && this.handleClose();
          }));
      }),
      this.$listen(this.close, "click", () => {
        this.handleClose();
      }));
  }
  handlePopup() {
    (this.popup.classList.add("is-active"),
      (document.body.style.overflow = "hidden"),
      this.player.togglePlay());
  }
  handleClose() {
    (this.popup.classList.remove("is-active"),
      (document.body.style.overflow = "auto"),
      this.player.pause());
  }
}
setFunctionName(VideoPlayer, "VideoPlayer");
const propertyTypes = {
  Boolean,
  String,
};
VideoPlayer.prototype.$name = "VideoPlayer";
VideoPlayer.prototype.$info = JSON.parse(
  '{"name":"VideoPlayer","meta":{"isLegacy":false,"selector":"video-player"},"props":[{"attr":"background","reflectAttr":false,"propertyKey":"background","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"unmute","reflectAttr":false,"propertyKey":"unmute","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"},{"attr":"id","reflectAttr":false,"propertyKey":"id","propertyType":"String","propertyTypeName":"String","propertyTsTypeName":"string"},{"attr":"plyer-wrapper","default":true,"reflectAttr":false,"propertyKey":"plyerWrapper","propertyType":"Boolean","propertyTypeName":"Boolean","propertyTsTypeName":"boolean"}],"refs":[{"selector":"[data-ref=\\"video\\"]","required":false,"propertyKey":"video","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLVideoElement|HTMLIFrameElement|HTMLDivElement"},{"selector":"[data-ref=\\"trigger\\"]","required":false,"propertyKey":"trigger","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLButtonElement"},{"selector":"[data-ref=\\"close\\"]","required":false,"propertyKey":"close","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLButtonElement"},{"selector":"[data-ref=\\"popup\\"]","required":false,"propertyKey":"popup","propertyType":"Object","propertyTypeName":"Object","propertyTsTypeName":"HTMLDivElement"}]}',
);
VideoPlayer.prototype.$info.component = VideoPlayer;
for (const prop of VideoPlayer.prototype.$info.props)
  prop.propertyType = propertyTypes[prop.propertyType];
window.componentService.registerComponent(
  "VideoPlayer/VideoPlayer.ts",
  {
    VideoPlayer: VideoPlayer,
  },
  void 0,
);
